import React, { useEffect, useMemo, useState } from 'react';
import { KeyRound, Loader2, RefreshCw, Search, ShieldCheck, X } from 'lucide-react';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Sidebar';
import BrandLoader from '../../components/BrandLoader';
import Navbar from '../../components/Navbar';
import API from '../../utils/api';
import './AccountPasswords.css';

const PAGE_SIZE = 10;

// Meets the password policy: 6+ chars with an uppercase letter, a number and a symbol.
const generatePassword = () => {
    const pick = chars => chars[Math.floor(Math.random() * chars.length)];
    const all = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789';
    const chars = [pick('ABCDEFGHJKMNPQRSTUVWXYZ'), pick('23456789'), pick('@#$%&*!'), ...Array.from({ length: 7 }, () => pick(all))];
    return chars.sort(() => Math.random() - 0.5).join('');
};

const AccountPasswords = () => {
    const [tab, setTab] = useState('member');
    const [members, setMembers] = useState([]);
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    const [target, setTarget] = useState(null);
    const [password, setPassword] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        Promise.all([API.get('/admin/users'), API.get('/admin/vendors')])
            .then(([u, v]) => {
                setMembers((u.data.users || []).filter(x => x.role === 'member' || !x.role));
                setProviders(v.data.vendors || []);
            })
            .catch(() => toast.error('Could not load accounts'))
            .finally(() => setLoading(false));
    }, []);

    const rows = useMemo(() => {
        const list = tab === 'member' ? members : providers;
        const q = query.trim().toLowerCase();
        if (!q) return list;
        return list.filter(a => [a.name, a.businessName, a.email, a.mobile].some(v => String(v || '').toLowerCase().includes(q)));
    }, [tab, members, providers, query]);

    const [page, setPage] = useState(1);
    useEffect(() => { setPage(1); }, [tab, query]);
    const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const pageRows = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    const openReset = account => { setTarget(account); setPassword(generatePassword()); };

    const save = async event => {
        event.preventDefault();
        setSaving(true);
        try {
            const res = await API.put(`/admin/users/${target._id}/password`, { newPassword: password });
            toast.success(res.data.message || 'Password updated');
            setTarget(null);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Could not update password');
        } finally {
            setSaving(false);
        }
    };

    const label = a => a.businessName || a.name || a.profileName || 'Unnamed';

    return (
        <div className="acct-pw-layout">
            <Sidebar />
            <main className="admin-content acct-pw-content">
                <Navbar />
                <div className="acct-pw-page">
                    <header className="acct-pw-heading">
                        <div className="acct-pw-eyebrow"><KeyRound size={14} /> Users &amp; Access</div>
                        <h1>Account passwords</h1>
                        <p>Set a new password for a member or service provider. Existing passwords are stored as one-way hashes and cannot be viewed.</p>
                    </header>

                    <div className="acct-pw-toolbar">
                        <div className="acct-pw-tabs">
                            <button type="button" className={tab === 'member' ? 'is-active' : ''} onClick={() => setTab('member')}>Members ({members.length})</button>
                            <button type="button" className={tab === 'service' ? 'is-active' : ''} onClick={() => setTab('service')}>Service providers ({providers.length})</button>
                        </div>
                        <label className="acct-pw-search">
                            <Search size={16} />
                            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search name, email or mobile" />
                        </label>
                    </div>

                    <div className="acct-pw-table-wrap">
                        {loading ? (
                            <div className="acct-pw-empty"><BrandLoader size={44} /></div>
                        ) : rows.length === 0 ? (
                            <div className="acct-pw-empty">No accounts found</div>
                        ) : (
                            <table className="acct-pw-table">
                                <thead><tr><th>Name</th><th>Mobile</th><th>Email</th><th>Password</th><th /></tr></thead>
                                <tbody>
                                    {pageRows.map(a => (
                                        <tr key={a._id}>
                                            <td>{label(a)}</td>
                                            <td>{a.mobile || '—'}</td>
                                            <td>{a.email || '—'}</td>
                                            <td className="acct-pw-hidden"><ShieldCheck size={14} /> ••••••••</td>
                                            <td><button type="button" className="acct-pw-reset" onClick={() => openReset(a)}>Set new password</button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {rows.length > PAGE_SIZE && (
                        <div className="acct-pw-pager">
                            <span>{(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, rows.length)} of {rows.length}</span>
                            <button type="button" onClick={() => setPage(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
                            <span>Page {currentPage} / {totalPages}</span>
                            <button type="button" onClick={() => setPage(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
                        </div>
                    )}
                </div>

                {target && (
                    <div className="acct-pw-overlay" onClick={() => !saving && setTarget(null)}>
                        <form className="acct-pw-modal" onClick={e => e.stopPropagation()} onSubmit={save}>
                            <button type="button" className="acct-pw-close" onClick={() => setTarget(null)} aria-label="Close"><X size={18} /></button>
                            <h2>Set new password</h2>
                            <p>{label(target)} · {target.mobile}</p>
                            <label className="acct-pw-field">
                                <span>New password</span>
                                <div>
                                    <input type="text" value={password} onChange={e => setPassword(e.target.value)} autoComplete="off" required />
                                    <button type="button" onClick={() => setPassword(generatePassword())} title="Generate" aria-label="Generate password"><RefreshCw size={16} /></button>
                                </div>
                            </label>
                            <small>At least 6 characters with an uppercase letter, a number and a symbol. Share it with the user securely; they can change it after signing in.</small>
                            <button type="submit" className="acct-pw-save" disabled={saving}>
                                {saving ? <Loader2 size={16} className="acct-pw-spin" /> : <KeyRound size={16} />} Update password
                            </button>
                        </form>
                    </div>
                )}
            </main>
        </div>
    );
};

export default AccountPasswords;
